import { SupabaseClient } from '@supabase/supabase-js';
import { Recipe } from './Recipe';
import { InAppNotification } from './Types';
import { ApiServiceContract, ApiServiceModules } from './apiService.contract';
import type {
  AuthorApi,
  CommentApi,
  InfrastructureApi,
  NotificationApi,
  RecipeApi,
  RecipeRealizationApi,
  StorageApi,
  TagApi,
} from './contracts';

interface SupabaseClientProvider {
  getClient: () => Promise<SupabaseClient>;
}

interface StorageApiDependencies {
  transformImageUrl: (url: string | null | undefined) => string | null;
  reverseTransformImageUrl: (url: string) => string;
  extractFilePathFromImageUrl: (imageUrl: string) => string | null;
  triggerImageDelete: (filePath: string) => Promise<void>;
  uploadImage: (file: File, directoryPrefix?: string) => Promise<string>;
}

interface NotificationApiDependencies {
  listMyNotifications: (limit: number) => Promise<InAppNotification[]>;
  getUnreadCount: () => Promise<number>;
  markAsRead: (notificationId: number) => Promise<boolean>;
  markAllAsRead: () => Promise<number>;
}

interface ApiServiceDependencies {
  supabaseService: SupabaseClientProvider;
  authorService: AuthorApi;
  recipeService: Pick<
    RecipeApi,
    'getDailySelectionRecipe' | 'getAllRecipes' | 'getRecipeById' | 'addRecipe' | 'updateRecipe' | 'updateRecipeImage' | 'deleteRecipe'
  >;
  favoriteService: Pick<RecipeApi, 'listMyFavorites' | 'addFavorite' | 'addFavorites' | 'removeFavorite'>;
  tagService: TagApi;
  commentService: CommentApi;
  recipeRealizationService: RecipeRealizationApi;
  inAppNotificationService: NotificationApiDependencies;
  storageService: StorageApiDependencies;
}

type VoidMethodNames<TService extends object> = {
  [TKey in keyof TService]: TService[TKey] extends (...args: any[]) => Promise<void> ? TKey : never;
}[keyof TService];

const createInfrastructureApi = (
  dependencies: ApiServiceDependencies
): InfrastructureApi => ({
  _getSupabaseClient: (): Promise<SupabaseClient> => dependencies.supabaseService.getClient(),
});

const createStorageApi = (
  dependencies: ApiServiceDependencies
): StorageApi => ({
  transformImageUrl: (url: string | null | undefined): string | null =>
    dependencies.storageService.transformImageUrl(url),
  reverseTransformImageUrl: (url: string): string =>
    dependencies.storageService.reverseTransformImageUrl(url),
  _extractFilePathFromImageUrl: (imageUrl: string): string | null =>
    dependencies.storageService.extractFilePathFromImageUrl(imageUrl),
  _triggerImageDelete: async (filePath: string): Promise<void> => {
    await dependencies.storageService.triggerImageDelete(filePath);
  },
  uploadImage: (file: File, directoryPrefix?: string): Promise<string> =>
    dependencies.storageService.uploadImage(file, directoryPrefix),
});

const bindMethods = <
  TService extends object,
  TMethodNames extends ReadonlyArray<keyof TService>
>(
  service: TService,
  methodNames: TMethodNames
): Pick<TService, TMethodNames[number]> => {
  return methodNames.reduce((boundMethods, methodName) => {
    const serviceMethod = service[methodName];

    if (typeof serviceMethod === 'function') {
      (boundMethods as Record<string, unknown>)[methodName as string] =
        (...args: unknown[]) => (serviceMethod as (...innerArgs: unknown[]) => unknown).apply(service, args);
    }

    return boundMethods;
  }, {} as Pick<TService, TMethodNames[number]>);
};

const bindVoidMethods = <
  TService extends object,
  TMethodNames extends ReadonlyArray<VoidMethodNames<TService>>
>(
  service: TService,
  methodNames: TMethodNames
): Pick<TService, TMethodNames[number]> => {
  return methodNames.reduce((boundMethods, methodName) => {
    const serviceMethod = service[methodName];

    if (typeof serviceMethod === 'function') {
      (boundMethods as Record<string, unknown>)[methodName as string] =
        async (...args: unknown[]): Promise<void> => {
          await (serviceMethod as (...innerArgs: unknown[]) => Promise<void>).apply(service, args);
        };
    }

    return boundMethods;
  }, {} as Pick<TService, TMethodNames[number]>);
};

const createAuthorApi = (
  dependencies: ApiServiceDependencies
): AuthorApi => bindMethods(dependencies.authorService, [
  'validateTokenAndGetAuthor',
  'getAuthorById',
  'getAllAuthors',
  'updateAuthor',
  'getMyNotificationPreferences',
  'updateMyNotificationPreferences',
] as const);

const bindRecipeServiceMethods = (
  recipeService: ApiServiceDependencies['recipeService']
) => bindMethods(recipeService, [
  'getDailySelectionRecipe',
  'getRecipeById',
  'addRecipe',
  'updateRecipe',
  'updateRecipeImage',
] as const);

const bindFavoriteServiceMethods = (
  favoriteService: ApiServiceDependencies['favoriteService']
) => ({
  ...bindMethods(favoriteService, ['listMyFavorites'] as const),
  ...bindVoidMethods(favoriteService, ['addFavorite', 'addFavorites', 'removeFavorite'] as const),
});

const createRecipeApi = (
  dependencies: ApiServiceDependencies
): RecipeApi => ({
  ...bindRecipeServiceMethods(dependencies.recipeService),
  getAllRecipes: (
    offset: number = 0,
    limit: number = 10,
    selectedTagIds: number[] = [],
    selectedAuthorIds: number[] = [],
    sortKey: string = 'created_at_desc'
  ): Promise<Recipe[]> =>
    dependencies.recipeService.getAllRecipes(
      offset,
      limit,
      selectedTagIds,
      selectedAuthorIds,
      sortKey
    ),
  ...bindFavoriteServiceMethods(dependencies.favoriteService),
  ...bindVoidMethods(dependencies.recipeService, ['deleteRecipe'] as const),
});

const createTagApi = (
  dependencies: ApiServiceDependencies
): TagApi => ({
  ...bindMethods(dependencies.tagService, ['getTags', 'getUsedTags', 'addTag'] as const),
  ...bindVoidMethods(dependencies.tagService, ['deleteTag'] as const),
});

const createCommentApi = (
  dependencies: ApiServiceDependencies
): CommentApi => ({
  ...bindMethods(dependencies.commentService, ['getComments', 'getCommentById', 'addComment', 'updateComment'] as const),
  ...bindVoidMethods(dependencies.commentService, ['deleteComment'] as const),
});

const createRecipeRealizationApi = (
  dependencies: ApiServiceDependencies
): RecipeRealizationApi => ({
  ...bindMethods(dependencies.recipeRealizationService, [
    'listRecipeRealizations',
    'upsertMyRecipeRealization',
    'uploadRealizationImage',
  ] as const),
  ...bindVoidMethods(dependencies.recipeRealizationService, [
    'deleteMyRecipeRealization',
    'deleteRealizationImage',
  ] as const),
});

const createNotificationApi = (
  dependencies: ApiServiceDependencies
): NotificationApi => ({
  listMyNotifications: (limit: number = 20): Promise<InAppNotification[]> =>
    dependencies.inAppNotificationService.listMyNotifications(limit),
  getMyUnreadNotificationsCount: (): Promise<number> =>
    dependencies.inAppNotificationService.getUnreadCount(),
  markMyNotificationAsRead: (notificationId: number): Promise<boolean> =>
    dependencies.inAppNotificationService.markAsRead(notificationId),
  markMyNotificationsAsRead: (): Promise<number> =>
    dependencies.inAppNotificationService.markAllAsRead(),
});

export const createApiService = (
  dependencies: ApiServiceDependencies
): ApiServiceContract => {
  const apiModules = createApiServiceModules(dependencies);

  return {
    ...apiModules.infrastructure,
    ...apiModules.storage,
    ...apiModules.authors,
    ...apiModules.recipes,
    ...apiModules.tags,
    ...apiModules.comments,
    ...apiModules.recipeRealizations,
    ...apiModules.notifications,
  };
};

export const createApiServiceModules = (
  dependencies: ApiServiceDependencies
): ApiServiceModules => ({
  infrastructure: createInfrastructureApi(dependencies),
  storage: createStorageApi(dependencies),
  authors: createAuthorApi(dependencies),
  recipes: createRecipeApi(dependencies),
  tags: createTagApi(dependencies),
  comments: createCommentApi(dependencies),
  recipeRealizations: createRecipeRealizationApi(dependencies),
  notifications: createNotificationApi(dependencies),
});
